import React, { FC, useMemo } from 'react';
import {
  Button,
  Card,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCustomers, useCustomersHierarchy } from '@/api/Customers';
import type { CustomerHierarchy } from '@/types';
import {
  useDeactivateTemplate,
  usePublishTemplate,
  useTemplateList,
} from '@/api/QuestionnaireTemplates';
import type { TemplateSummary } from '@/api/QuestionnaireTemplates';

interface TemplateListProps {
  onNew: () => void;
  onEdit: (id: number) => void;
}

const uniqueFilters = (values: string[]) =>
  Array.from(new Set(values)).map((value) => ({ text: value, value }));

export const TemplateList: FC<TemplateListProps> = ({ onNew, onEdit }) => {
  const { data: customers = [], isFetching: customersFetching } =
    useCustomers();
  const customerIds = customers.map((c) => c.customerId);
  const {
    data: templates = [],
    isFetching: templatesFetching,
    refetch,
  } = useTemplateList(customerIds, 'perScope');
  const { data: hierarchy = [] } = useCustomersHierarchy();
  const { mutateAsync: deactivate, isPending: deactivating } =
    useDeactivateTemplate();
  const { mutateAsync: publish, isPending: publishing } = usePublishTemplate();

  const loading =
    customersFetching || templatesFetching || deactivating || publishing;

  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    customers.forEach((c) => map.set(c.customerId, c.customerName));
    const walk = (nodes: CustomerHierarchy[]) => {
      nodes.forEach((node) => {
        map.set(node.customerId, node.customerName);
        if (node.children) walk(node.children);
      });
    };
    walk(hierarchy);
    return map;
  }, [customers, hierarchy]);

  const scopeLabel = (customerId?: number) =>
    customerId == null
      ? 'Global'
      : (nameById.get(customerId) ?? `#${customerId}`);

  const customerCell = (row: TemplateSummary) => {
    if (row.customerId == null) return 'Global';
    const base = scopeLabel(row.customerId);
    if (row.subCustomerId == null) return base;
    const subName = nameById.get(row.subCustomerId) ?? `#${row.subCustomerId}`;
    return `${base} › ${subName}`;
  };

  const onDeactivate = async (id: number) => {
    const res = await deactivate(id);
    if (!res.success) {
      message.error(res.message?.Message ?? 'Deactivate failed.');
      return;
    }
    await refetch();
  };

  const onPublish = async (id: number) => {
    const res = await publish(id);
    if (!res.success) {
      message.error(res.message?.Message ?? 'Publish failed.');
      return;
    }
    await refetch();
  };

  const columns: ColumnsType<TemplateSummary> = [
    {
      title: 'Key',
      dataIndex: 'templateKey',
      key: 'templateKey',
      filters: uniqueFilters(templates.map((t) => t.templateKey)),
      onFilter: (value, row) => row.templateKey === value,
    },
    {
      title: 'Title',
      key: 'title',
      filters: uniqueFilters(templates.map((t) => t.title || t.templateKey)),
      onFilter: (value, row) => (row.title || row.templateKey) === value,
      render: (_, row) => (
        <Space>
          <span>{row.title || row.templateKey}</span>
          {row.pendingDraftVersion != null ? (
            <Tag color="gold">{`Draft v${row.pendingDraftVersion}`}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      filters: uniqueFilters(templates.map((t) => customerCell(t))),
      onFilter: (value, row) => customerCell(row) === value,
      render: (_, row) => customerCell(row),
    },
    { title: 'Version', dataIndex: 'version', key: 'version' },
    {
      title: 'Status',
      key: 'status',
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, row) => row.status === value,
      render: (_, row) =>
        row.status === 'Active' ? (
          <Popconfirm
            title="Deactivate this template?"
            onConfirm={() => onDeactivate(row.id)}
          >
            <Switch checked />
          </Popconfirm>
        ) : (
          <Switch
            checked={false}
            disabled={row.status === 'Inactive'}
            onChange={
              row.status === 'Draft' ? () => onPublish(row.id) : undefined
            }
          />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button onClick={() => onEdit(row.pendingDraftId ?? row.id)}>
            Edit
          </Button>
          {row.pendingDraftId != null ? (
            <Button onClick={() => onPublish(row.pendingDraftId!)}>
              {`Publish v${row.pendingDraftVersion}`}
            </Button>
          ) : null}
          {row.status === 'Draft' ? (
            <Button onClick={() => onPublish(row.id)}>Publish</Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Card
      extra={
        <Button type="primary" onClick={onNew}>
          New Template
        </Button>
      }
      styles={{ body: { padding: 16 } }}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={templates}
        loading={loading}
        pagination={false}
      />
    </Card>
  );
};
